import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@monksflow/monks-ui';
import { useId } from 'react';
import type {
  PersonaSelectFieldProps,
  PersonaTextFieldProps,
  PersonaTextareaFieldProps,
} from './PersonaFields.types';

/**
 * Controlled field primitives for the redesigned persona forms, composed on
 * Tessra's `Field` set.
 *
 * They exist alongside `@/shared/ui/form`'s MUI equivalents rather than
 * replacing them: those still serve every form on the other side of the
 * redesign flag.
 */

const RequiredMark = () => <span className="text-destructive"> *</span>;

export const PersonaTextField = ({
  label,
  value,
  onChange,
  onBlur,
  name,
  placeholder,
  required,
  error,
  disabled,
  description,
}: PersonaTextFieldProps) => {
  const id = useId();

  return (
    <Field data-invalid={Boolean(error) || undefined}>
      <FieldLabel htmlFor={id}>
        {label}
        {required && <RequiredMark />}
      </FieldLabel>
      <Input
        id={id}
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={Boolean(error) || undefined}
      />
      {error ? (
        <FieldError>{error}</FieldError>
      ) : description ? (
        <FieldDescription>{description}</FieldDescription>
      ) : null}
    </Field>
  );
};

PersonaTextField.displayName = 'PersonaTextField';

export const PersonaTextareaField = ({
  label,
  value,
  onChange,
  onBlur,
  name,
  placeholder,
  required,
  error,
  disabled,
  rows,
}: PersonaTextareaFieldProps) => {
  const id = useId();

  return (
    <Field data-invalid={Boolean(error) || undefined}>
      <FieldLabel htmlFor={id}>
        {label}
        {required && <RequiredMark />}
      </FieldLabel>
      <Textarea
        id={id}
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        aria-invalid={Boolean(error) || undefined}
      />
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
};

PersonaTextareaField.displayName = 'PersonaTextareaField';

export const PersonaSelectField = ({
  label,
  value,
  onChange,
  name,
  placeholder,
  required,
  error,
  disabled,
  options,
}: PersonaSelectFieldProps) => {
  const id = useId();

  return (
    <Field data-invalid={Boolean(error) || undefined}>
      <FieldLabel htmlFor={id}>
        {label}
        {required && <RequiredMark />}
      </FieldLabel>
      {/* Radix Select treats "" as "no selection", which is what an unset form
          field holds, so the empty string must not reach it as a value. */}
      <Select name={name} value={value || undefined} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={id} aria-invalid={Boolean(error) || undefined}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
};

PersonaSelectField.displayName = 'PersonaSelectField';
